import { X, AlertCircle } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

import {
  inputContainerVariants,
  inputLabelVariants,
  inputRequiredVariants,
  inputWrapperVariants,
  inputContentContainerVariants,
  inputFieldVariants,
  inputPrefixVariants,
  inputSuffixVariants,
  inputIconVariants,
  inputClearButtonVariants,
  inputHelperTextVariants,
  inputHelperIconVariants,
  inputLoadingIndicatorVariants,
  inputLoadingTrackVariants,
} from './Input.styles';

import type {
  InputProps,
  InputLabelProps,
  InputFieldProps,
  InputPrefixProps,
  InputSuffixProps,
  InputIconProps,
  InputClearButtonProps,
  InputHelperTextProps,
  InputLoadingIndicatorProps,
} from './Input.types';

/**
 * Main Input component with label, helper text, and all states
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   placeholder="Enter your email"
 *   type="email"
 *   required
 *   helperText="We'll never share your email"
 * />
 * ```
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'm',
      type = 'text',
      label,
      required = false,
      optional = false,
      helperText,
      error,
      disabled = false,
      readOnly = false,
      loading = false,
      leadingIcon,
      trailingIcon,
      prefix,
      suffix,
      clearable = false,
      onClear,
      className,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedby,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue || '');

    // Determine if controlled or uncontrolled
    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    const hasValue = !!currentValue;
    const hasError = !!error;
    const showClearButton = clearable && hasValue && !disabled && !readOnly;

    // Generate IDs for ARIA
    const inputId = React.useId();
    const helperTextId = `${inputId}-helper`;
    const errorId = `${inputId}-error`;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(event.target.value);
      }
      onChange?.(event);
    };

    const handleClear = () => {
      if (!isControlled) {
        setInternalValue('');
      }
      onClear?.();

      // Dispatch change event for controlled inputs
      const input = ref && 'current' in ref ? ref.current : null;
      if (input) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        nativeInputValueSetter?.call(input, '');

        const event = new Event('input', { bubbles: true });
        input.dispatchEvent(event);
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
    };

    return (
      <div className={cn(inputContainerVariants({ disabled }), className)}>
        {label && (
          <InputLabel
            htmlFor={inputId}
            size={size}
            required={required}
            optional={optional}
            disabled={disabled}
          >
            {label}
          </InputLabel>
        )}

        <div className={cn(inputWrapperVariants({ size }))}>
          {prefix && <InputPrefix size={size}>{prefix}</InputPrefix>}

          <div
            className={cn(
              inputContentContainerVariants({
                size,
                hasError,
                readOnly,
                disabled,
              })
            )}
          >
            {loading && <InputLoadingIndicator loading={loading} />}

            {leadingIcon && (
              <InputIcon size={size} position="leading">
                {leadingIcon}
              </InputIcon>
            )}

            <InputField
              ref={ref}
              id={inputId}
              type={type}
              size={size}
              value={currentValue}
              readOnly={readOnly}
              disabled={disabled}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              aria-label={ariaLabel}
              aria-describedby={
                ariaDescribedby ||
                (helperText ? helperTextId : undefined) ||
                (error ? errorId : undefined)
              }
              aria-invalid={ariaInvalid || hasError}
              {...props}
            />

            {showClearButton && (
              <InputClearButton
                size={size}
                onClick={handleClear}
                aria-label="Clear input"
                disabled={disabled}
              />
            )}

            {!showClearButton && trailingIcon && (
              <InputIcon size={size} position="trailing">
                {trailingIcon}
              </InputIcon>
            )}
          </div>

          {suffix && <InputSuffix size={size}>{suffix}</InputSuffix>}
        </div>

        {(helperText || error) && (
          <InputHelperText id={error ? errorId : helperTextId} error={hasError} size={size}>
            {error || helperText}
          </InputHelperText>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

/**
 * InputLabel component
 * Displays label text with optional required indicator
 */
export const InputLabel = React.forwardRef<HTMLLabelElement, InputLabelProps>(
  (
    {
      children,
      required = false,
      optional = false,
      size = 'm',
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    return (
      <label ref={ref} className={cn(inputLabelVariants({ size, disabled }), className)} {...props}>
        <span>{children}</span>
        {required && <span className={cn(inputRequiredVariants({ size }))}>*</span>}
        {optional && <span className="ms-auto text-foreground-muted">{t('Optional')}</span>}
      </label>
    );
  }
);

InputLabel.displayName = 'InputLabel';

/**
 * InputField component
 * The actual input element
 */
export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  ({ size = 'm', readOnly = false, disabled = false, className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(inputFieldVariants({ size, readOnly, disabled }), className)}
      readOnly={readOnly}
      disabled={disabled}
      {...props}
    />
  )
);

InputField.displayName = 'InputField';

/**
 * InputPrefix component
 * Displays prefix text before the input
 */
export const InputPrefix = React.forwardRef<HTMLDivElement, InputPrefixProps>(
  ({ children, size = 'm', className, ...props }, ref) => (
    <div ref={ref} className={cn(inputPrefixVariants({ size }), className)} {...props}>
      {children}
    </div>
  )
);

InputPrefix.displayName = 'InputPrefix';

/**
 * InputSuffix component
 * Displays suffix text after the input
 */
export const InputSuffix = React.forwardRef<HTMLDivElement, InputSuffixProps>(
  ({ children, size = 'm', className, ...props }, ref) => (
    <div ref={ref} className={cn(inputSuffixVariants({ size }), className)} {...props}>
      {children}
    </div>
  )
);

InputSuffix.displayName = 'InputSuffix';

/**
 * InputIcon component
 * Displays icon element inside the input (leading or trailing)
 */
export const InputIcon = React.forwardRef<HTMLDivElement, InputIconProps>(
  ({ children, size = 'm', position: _position = 'leading', className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(inputIconVariants({ size }), className)}
      aria-hidden="true"
      {...props}
    >
      {children}
    </div>
  )
);

InputIcon.displayName = 'InputIcon';

/**
 * InputClearButton component
 * Clear button that appears when input has value
 */
export const InputClearButton = React.forwardRef<HTMLButtonElement, InputClearButtonProps>(
  ({ size = 'm', onClick, className, 'aria-label': ariaLabel = 'Clear input', ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(inputClearButtonVariants({ size }), className)}
      onClick={onClick}
      aria-label={ariaLabel}
      {...props}
    >
      <X className="w-full h-full" />
    </button>
  )
);

InputClearButton.displayName = 'InputClearButton';

/**
 * InputHelperText component
 * Displays helper text or error message below the input
 */
export const InputHelperText = React.forwardRef<HTMLParagraphElement, InputHelperTextProps>(
  ({ children, error = false, size = 'm', className, ...props }, ref) => (
    <div ref={ref} className={cn(inputHelperTextVariants({ error, size }), className)} {...props}>
      {error && (
        <div className={cn(inputHelperIconVariants())}>
          <AlertCircle className="w-full h-full" />
        </div>
      )}
      <p className="flex-1">{children}</p>
    </div>
  )
);

InputHelperText.displayName = 'InputHelperText';

/**
 * InputLoadingIndicator component
 * Displays loading progress bar at the top of the input
 */
export const InputLoadingIndicator = React.forwardRef<HTMLDivElement, InputLoadingIndicatorProps>(
  ({ loading = false, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(inputLoadingIndicatorVariants({ loading }), className)}
      aria-hidden="true"
      {...props}
    >
      <div className={cn(inputLoadingTrackVariants({ active: false }))} />
      <div className={cn(inputLoadingTrackVariants({ active: false }))} />
      <div className={cn(inputLoadingTrackVariants({ active: true }))} />
      <div className={cn(inputLoadingTrackVariants({ active: true }))} />
      <div className={cn(inputLoadingTrackVariants({ active: false }))} />
    </div>
  )
);

InputLoadingIndicator.displayName = 'InputLoadingIndicator';
